import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { formValues, schema } from "./validations";
import { CHECKLIST, SAFETY_CHECKLIST } from "./consts";
import { getRequiredFields, planStatus, removeEmptyFields } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import { useAddSafetyCheckMutation, useGetSafetyChecklistWithItemByProject, useGetSafetyCheckUpdates, useGetSafetyChecks, useUpdateSafetyCheckMutation } from "@/services";
import { useParams } from "react-router-dom";
import { toast } from "@/components";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useQueryClient } from "@tanstack/react-query";
import { routes } from "@/routes/utils";
import { setChecksData } from "@/redux/slices";

const useSafetyCompliance = () => {
  const {id, action, planAction} = useParams();
  const isView = action == routes.view || planAction == routes.view;
  const queryClient = useQueryClient();
  const [openTabs, setOpenTabs] = useState<any>("1");
  const [isPhotoSliderOpen, setIsPhotoSliderOpen] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState<any[]>([]);
  const [activeCheckForSlider, setActiveCheckForSlider] = useState<any>(null);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);
  const [clickedType, setClickedType] = useState<"attachment" | "notes" | null>(null);

  const dispatch = useDispatch();
  const form = useForm<formValues>({
    defaultValues: {
      [SAFETY_CHECKLIST]: [],
      [CHECKLIST]: {},
    },
    resolver: zodResolver(schema),
  });


  const requiredFields = getRequiredFields(schema);

  const {taskData, planData} = useSelector((state: RootState) => state.currentData)
  
  const isPlanApproved = useMemo(() => planData?.status == planStatus.approved.value, [planData]);

  const {data: safetyChecklist, isLoading: isLoadingChecklist} = useGetSafetyChecklistWithItemByProject({projectId: id as string})

  const {data: safetyChecks, isLoading: isLoadingChecks} = useGetSafetyChecks({
    projectId: id as string,
    taskId: taskData?.taskId
  })

  const { data: attachmentList, isLoading: isAttachmentsLoading } = useGetSafetyCheckUpdates({
    taskId: taskData?.taskId,
    projectId: id as string,
    checkId: activeCheckForSlider?.taskSafetyCheckId,
    queryParams: {
      enabled: !!activeCheckForSlider?.taskSafetyCheckId
    }
  })

  useEffect(() => {
    if (attachmentList?.data && activeCheckForSlider && clickedType) {
      if (clickedType === "attachment") {
        const allPhotos = attachmentList.data.reduce((acc: any[], update: any) => {
          const photosFromUpdate = update.attachments
            ?.filter((a: any) => a.presignedUrl)
            ?.map((a: any) => ({
              url: a.presignedUrl,
              description: a.fileDescription || update.description || "No description provided",
            }));
          
          return [...acc, ...(photosFromUpdate || [])];
        }, []);

        if (allPhotos.length > 0) {
          setSelectedPhotos(allPhotos);
          setIsPhotoSliderOpen(true);
        } else {
          toast({ message: "Not attachment found", type: "warn" });
        }
      } else if (clickedType === "notes") {
        setIsNotesDialogOpen(true);
      }
      setActiveCheckForSlider(null); 
      setClickedType(null);
    } else if (attachmentList && !attachmentList.data?.length && activeCheckForSlider && clickedType) {
       if (clickedType === "attachment") {
          toast({ message: "Not attachment found", type: "warn" });
       } else {
          setIsNotesDialogOpen(true);
       }
       setActiveCheckForSlider(null);
       setClickedType(null);
    }
  }, [attachmentList, activeCheckForSlider, clickedType]);

  useEffect(() => {
    if (safetyChecklist?.data &&  safetyChecks?.data){
      const groupedByChecklistId = safetyChecks?.data?.reduce(
        (acc: any, item: any, index: number) => {
          const key = item.taskSafetyChecklistId;

          const checklistData = safetyChecklist?.data?.find((val: any) => val?.taskSafetyChecklistId == key)

          if (!acc[key]) {
            acc[key] = {
              accordionId: (index + 1)?.toString(),
              taskSafetyChecklistId: key,
              ...checklistData,
              items: [],
            };
          }
      
          acc[key].items.push(item);

          const items = [...acc[key].items];

          acc[key].items = items?.map((val: any) => {
            return {
              ...val,
              ...checklistData.items.find((check: any) => check.taskSafetyChecklistItemId == val.taskSafetyChecklistItemId)
            }
          })
      
          return acc;
        },
        {}
      );
      form.setValue(CHECKLIST, groupedByChecklistId)
    }
  }, [safetyChecklist, safetyChecks]);
  
  const onAccordionChange = (e: any) => {
    setOpenTabs([e[e.length - 1]]);
  };

  const { mutateAsync: addInfo, isPending: isAddPending } =
    useAddSafetyCheckMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        // toast({ message: "Quality Checks Added Successfully" });
      },
    });

  const { mutateAsync: _updateInfo, isPending: isUpdatePending } =
    useUpdateSafetyCheckMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        // toast({ message: "Quality Checks Updated Successfully" });
      },
    });

    const onSubmit = async (data: formValues) => {
      const _data = removeEmptyFields(data);
  
      const promises: Promise<any>[] = [];
  
      // if (taskData?.taskId) {
      //   updateInfo({projectId: id as string, taskId: taskData?.taskId as string, 
      //     data: {
      //       ..._data,
      //   }})
      // } else {
        (_data[SAFETY_CHECKLIST] as any)?.map((val: any) => {
          safetyChecklist?.data?.find((check: any) => check.taskSafetyChecklistId == val)?.items?.map((check: any) => 
            promises.push(addInfo({projectId: id as string, taskId: taskData?.taskId as string, 
              data: {
                taskSafetyChecklistId: val,
                taskSafetyChecklistItemId: check?.taskSafetyChecklistItemId,
                status: "PENDING"
            }}))
          )
        })
      // }
  
      await Promise.all(promises);
      queryClient.invalidateQueries({queryKey: ["safety-checks", taskData?.taskId]});
      toast({ message: "Safety Checks Added Successfully" });
    }

    const onAttachmentClick = (checksData: any) => {
      dispatch(setChecksData(checksData));
      setActiveCheckForSlider(checksData);
      setClickedType("attachment");
    }

    const onNotesClick = (checksData: any) => {
      dispatch(setChecksData(checksData));
      setActiveCheckForSlider(checksData);
      setClickedType("notes");
    }

  return {
    form,
    requiredFields,
    openTabs,
    onAccordionChange,
    isLoadingChecklist,
    safetyChecklist,
    isAddPending,
    isUpdatePending,
    onSubmit,
    safetyChecks,
    isLoadingChecks,
    isView,
    onAttachmentClick,
    onNotesClick,
    isPhotoSliderOpen,
    setIsPhotoSliderOpen,
    isNotesDialogOpen,
    setIsNotesDialogOpen,
    selectedPhotos,
    isAttachmentsLoading,
    isPlanApproved
  };
};

export default useSafetyCompliance;
